/**
 * Selection Plugin Styles
 *
 * Scoped to tbw-grid using CSS nesting for light DOM compatibility.
 */
@layer tbw-plugins {
  tbw-grid {
    /* Prevent text selection during range drag */
    &.selecting .data-grid-row > .cell {
      user-select: none;
    }

    /* Prevent text selection when any selection exists */
    &:has(.selection) {
      user-select: none;
      /* Disable cell-focus outline in row mode - row is the focus unit.
       Also disable row-focus outline - selection plugin uses ::after borders instead. */
      &[data-selection-mode='row'] .cell-focus,
      &[data-selection-mode='row'] .row-focus,
      &[data-selection-mode='range'] .cell-focus {
        outline: none;
      }
    }

    /* Row selection - use accent color for row focus (only when grid is focused) */
    .data-grid-row.row-focus {
      background-color: var(--tbw-focus-background, rgba(from var(--tbw-color-accent) r g b / 12%));
      /* Replace outline with per-side pseudo-element borders that can collapse */
      outline: none;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        border-width: 0;
        border-style: var(--tbw-selection-border-style, var(--tbw-border-style));
        border-color: var(--tbw-range-border-color, var(--tbw-color-accent));
        border-top-width: var(--tbw-selection-border-width, var(--tbw-border-width));
        border-bottom-width: var(--tbw-selection-border-width, var(--tbw-border-width));
        /* Must paint above sticky cells (.sticky-left/.sticky-right have
           z-index: 25 in core/styles/base.css) so the selection border is
           continuous across pinned columns. The border ::after has
           pointer-events: none, so it doesn't interfere with cell content. */
        z-index: 26;
      }

      /* Collapse borders between adjacent selected rows */
      + .data-grid-row.row-focus::after {
        border-top-width: 0;
      }
      &:has(+ .data-grid-row.row-focus)::after {
        border-bottom-width: 0;
      }

      /* Sticky cells (.sticky-left/.sticky-right) have an opaque
         background-color to hide unpinned content scrolling beneath them
         (see core/styles/rows.css). That opaque layer also hides the
         row-focus background-color set above. Re-paint sticky cells with
         the focus tint layered over panel-bg so the selection tint is
         visible across pinned columns. The selection BORDER is handled by
         the row's ::after with z-index above the sticky cells — no need
         to redraw it here. */
      > .cell.sticky-left,
      > .cell.sticky-right {
        background:
          linear-gradient(var(--tbw-focus-background, rgba(from var(--tbw-color-accent) r g b / 12%)) 0 0),
          var(--tbw-color-panel-bg);
      }
    }

    /* Non-selectable selected rows use the row-alt background as the muted
       indicator — keep sticky cells in sync so pinned columns don't flash
       the accent tint on rows that aren't actually selectable. */
    .data-grid-row.row-focus[data-selectable='false'] {
      > .cell.sticky-left,
      > .cell.sticky-right {
        background: var(--tbw-color-row-alt);
      }
    }

    /* Selection cell styles - for range mode */
    .data-grid-row > .cell.selected {
      background-color: var(--tbw-range-selection-bg);
      position: relative;

      /* Pseudo-element draws the selection range border inside the cell's
         padding box. Uses inset: 0 to stay within overflow: hidden bounds.
         The cell's own borders (row divider, column border) remain visible
         outside the selection border — this is intentional and consistent
         across all themes regardless of border width or visibility. */
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        border: 0 var(--tbw-selection-border-style, var(--tbw-border-style)) var(--tbw-range-border-color);
        z-index: 1;
      }

      &.top::after {
        border-top-width: var(--tbw-selection-border-width, var(--tbw-border-width));
      }
      &.bottom::after {
        border-bottom-width: var(--tbw-selection-border-width, var(--tbw-border-width));
      }
      &.first::after {
        border-left-width: var(--tbw-selection-border-width, var(--tbw-border-width));
      }
      &.last::after {
        border-right-width: var(--tbw-selection-border-width, var(--tbw-border-width));
      }
    }

    /* Non-selectable row styling (row mode).
       The row is dimmed to signal "not selectable", but its *data* is still
       content the user has to read, so the exception WCAG 1.4.3 grants inactive
       UI components does not apply. 0.6 pushed the text to 4.24:1 on white;
       0.65 is the lowest value that clears AA (5.02:1). */
    .data-grid-row[data-selectable='false'] {
      cursor: not-allowed;
      opacity: var(--tbw-selection-disabled-opacity, 0.65);

      /* Muted selection indicator */
      &.row-focus {
        background-color: var(--tbw-color-row-alt);
      }
    }

    /* Non-selectable cell styling (cell/range mode) */
    .data-grid-row > .cell[data-selectable='false'] {
      cursor: not-allowed;
      opacity: var(--tbw-selection-disabled-opacity, 0.65);

      /* Prevent selection highlight on non-selectable cells */
      &.selected {
        background-color: var(--tbw-selection-warning-bg, rgba(from var(--tbw-color-error) r g b / 50%));
      }
    }

    /* Selection summary in header */
    .tbw-selection-summary {
      font-size: var(--tbw-font-size-sm, 0.8125rem);
      color: var(--tbw-color-fg-muted);
      white-space: nowrap;
    }

    /* Checkbox column styles */
    .data-grid-row > .cell[data-field='__tbw_checkbox'],
    .header-row > .cell[data-field='__tbw_checkbox'] {
      text-align: center;
      cursor: pointer;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* Row checkboxes are visual indicators - clicks pass through to cell */
    .tbw-select-row-checkbox {
      pointer-events: none;
      margin: 0;
      cursor: pointer;
    }

    /* Header select-all checkbox container */
    .tbw-checkbox-header {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      cursor: pointer;
    }

    .tbw-select-all-checkbox {
      margin: 0;
      cursor: pointer;
    }

    /* SC 2.5.8: the row checkbox is not itself a target — the row click selects,
       so the whole cell accepts the pointer. Select-all has its own handler, and
       gets a cell-sized target from the `<label>` wrapper instead of a 24px box.
       Coarse pointers still grow the boxes, which is a comfort choice. */
    @media (pointer: coarse) {
      .tbw-select-row-checkbox,
      .tbw-select-all-checkbox {
        min-width: var(--tbw-touch-target-min, 24px);
        min-height: var(--tbw-touch-target-min, 24px);
      }
    }

    /* ---- Column-axis selection ----------------------------------------- */
    /* Background tint for selected columns. Applied to the matching header
       cell and every data cell whose data-col resolves to a selected field.
       The tint uses --tbw-column-selection-bg (overridable) defaulting to
       a 12 % accent overlay — visually distinct from row-focus but uses the
       same palette so themes stay coherent. */
    .header-row > .cell.column-selected,
    .data-grid-row > .cell.column-selected {
      background:
        linear-gradient(var(--tbw-column-selection-bg, rgba(from var(--tbw-color-accent) r g b / 12%)) 0 0),
        var(--tbw-color-panel-bg);
    }

    /* Pinned columns repaint with sticky background; layer the column tint
       on top of the panel-bg fill so the indicator survives pinning. */
    .data-grid-row > .cell.column-selected.sticky-left,
    .data-grid-row > .cell.column-selected.sticky-right,
    .header-row > .cell.column-selected.sticky-left,
    .header-row > .cell.column-selected.sticky-right {
      background:
        linear-gradient(var(--tbw-column-selection-bg, rgba(from var(--tbw-color-accent) r g b / 12%)) 0 0),
        var(--tbw-color-panel-bg);
    }

    /* ========== Touch selection mode (#304) ========== */

    /* Sticky action bar shown while touch selection mode is active.  Rendered
       only on coarse-pointer devices, so the sizing targets a finger. */
    .tbw-selection-toolbar {
      position: sticky;
      top: 0;
      z-index: 30;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 4px 8px;
      background: var(--tbw-color-panel-bg);
      border-bottom: 1px solid var(--tbw-color-border);
      font-size: 13px;
    }

    .tbw-selection-toolbar-count {
      font-weight: 600;
      white-space: nowrap;
    }

    .tbw-selection-toolbar-actions {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    /* `--tbw-touch-target-min` is owned by the touch-target sub-issue (#305);
       the literal fallback keeps this file readable on its own. */
    .tbw-selection-toolbar-btn {
      min-height: var(--tbw-touch-target-min, 24px);
      padding: 0 10px;
      border: 0;
      /* Themes set radii for much larger surfaces (Material uses 12px), which
         turns a 24px control into a lozenge. */
      border-radius: min(var(--tbw-border-radius, 4px), 6px);
      background: transparent;
      color: inherit;
      font: inherit;
      cursor: pointer;

      &:hover,
      &:focus-visible {
        background: var(--tbw-color-row-hover, light-dark(#f0f0f0, #2a2a2a));
      }

      &[data-action='done'] {
        font-weight: 600;
        color: var(--tbw-color-accent-text, var(--tbw-color-accent));
      }
    }

    /* Draggable corner dots on an active cell range (iOS Numbers idiom).
       Additive — a mouse user gets them too. The visible dot is smaller than
       the hit area, and `touch-action` is off because the drag runs on pointer
       capture. */
    .tbw-range-handle {
      position: absolute;
      z-index: 25;
      width: var(--tbw-touch-target-min, 24px);
      height: var(--tbw-touch-target-min, 24px);
      translate: -50% -50%;
      background: radial-gradient(circle, var(--tbw-color-accent) 0 5px, transparent 5px);
      cursor: crosshair;
      touch-action: none;

      &[hidden] {
        display: none;
      }

      &.dragging {
        scale: 1.3;
      }

      /* Tapped rather than dragged: the handle is waiting for the cell that
         will receive this corner (WCAG 2.2 SC 2.5.7). */
      &.armed {
        scale: 1.3;
        outline: 2px solid var(--tbw-color-accent);
        outline-offset: -4px;
        border-radius: 50%;
      }
    }
  }
} /* End @layer tbw-plugins */
